<template>
    <mds-layout-grid :class="namespaceClass()">
        <mds-row>
            <mds-col>
                <div :class="namespaceClass('description')" v-if="isPortfolioComparison">
                    {{ this.getUILabel("time_period_description") }}
                </div>
            </mds-col>
        </mds-row>
        <mds-row>
            <mds-col :cols="4">
                <mds-form size="small" :class="namespaceClass('settings')">
                    <mds-fieldset variation="radio-group" hidden-legend>
                        <mds-radio-button
                            v-for="(option, index) in portfolioTimePeriodOptions"
                            :e2e="`time-period-${index}`"
                            :key="index"
                            :value="option.value"
                            v-model="portfolioTimePeriod"
                            :checked="portfolioTimePeriod === option.value"
                            :disabled="option.disabled"
                            @input="value => changeTimePeriod(value, option)"
                            ref="timePeriodRadios"
                            :microcopy="getDateRange(option)"
                        >
                            {{ option.text }}
                        </mds-radio-button>
                    </mds-fieldset>
                    <mds-fieldset hidden-legend horizontal>
                        <mds-input
                            e2e="custom-start-date"
                            :class="{ 'input--disabled': !isPortfolioDateEnabled }"
                            :disabled="!isPortfolioDateEnabled"
                            size="small"
                            :label="getUILabel('holdings_start_date')"
                            :id="portfolioStartDateId"
                            :value="customTimePeriodDateRange.startDate"
                            :placeholder="dataInputPlaceholder"
                            :error="errorToggle && tooltipTrigger === portfolioStartDateId"
                            @keypress="excludeInvalidChar"
                            @change="validateStartDate"
                        >
                        </mds-input>
                        <mds-input
                            e2e="custom-end-date"
                            :class="{ 'input--disabled': !isPortfolioDateEnabled }"
                            :disabled="!isPortfolioDateEnabled"
                            size="small"
                            :label="getUILabel('holdings_end_date')"
                            :id="portfolioEndDateId"
                            :value="customTimePeriodDateRange.endDate"
                            :error="errorToggle && tooltipTrigger === portfolioEndDateId"
                            :placeholder="dataInputPlaceholder"
                            @keypress="excludeInvalidChar"
                            @change="validateEndDate"
                        >
                        </mds-input>
                    </mds-fieldset>
                    <mds-fieldset variation="radio-group" hidden-legend v-if="isMultipleTimePeriod">
                        <mds-radio-button :checked="isMultipleTimePeriod">{{ getUILabel("COMMON_VAH") }}</mds-radio-button>
                    </mds-fieldset>
                </mds-form>
            </mds-col>
        </mds-row>
        <mds-row>
            <mds-col :cols="12">
                <mds-section border="tertiary" :title="sectionTitle" :size="7">
                    <div :class="namespaceClass('holdings-table')">
                        <mds-loader v-if="showLoading"></mds-loader>
                        <div v-else style="max-width: 1000px;">
                            <mds-data-table
                                fixed-first-col
                                row-numbers
                                e2e="time-period-holdings"
                                :header-configs="columnFields"
                                :row-data="tableData"
                                @hook:updated="cleanFixedFirstCol"
                                @hook:mounted="cleanFixedFirstCol"
                            >
                                <template v-slot:body>
                                    <mds-data-table-body :header-configs="columnFields" :row-data="tableData">
                                        <template v-slot:body-cell="{ headerConfig, rowData, rowIndex }">
                                            <template v-if="headerConfig.fieldName === 'securityName'">
                                                <div :title="rowData[headerConfig.fieldName]" class="name" :e2e="`time-period-holding-name-${rowIndex}`">
                                                    {{ rowData[headerConfig.fieldName] }}
                                                </div>
                                            </template>
                                            <template v-else-if="headerConfig.fieldName === 'timePeriod'">
                                                <template v-if="isSimpleAssumptionWorkflow || hypoType === 2">
                                                    {{ securityTimePeriodText(rowData) }}
                                                </template>
                                                <template v-else>
                                                    <mds-select
                                                        :e2e="`holding-time-period-${rowIndex}`"
                                                        :label="headerConfig.text"
                                                        :value="securityTimePeriod(rowData)"
                                                        hidden-label
                                                        @change="value => changeSecurityTimePeriod(value, rowData)"
                                                    >
                                                        <option
                                                            v-for="option in securityTimePeriodOptions(rowData)"
                                                            :key="option.value"
                                                            :value="option.value"
                                                            :disabled="option.disabled"
                                                        >
                                                            {{ option.text }}
                                                        </option>
                                                    </mds-select>
                                                </template>
                                            </template>
                                            <template v-else-if="headerConfig.fieldName === 'startDate'">
                                                <template v-if="rowData.timePeriod === 0 && !isSimpleAssumptionWorkflow && hypoType !== 2">
                                                    <hypo-date-input
                                                        :key="rowIndex"
                                                        :label="headerConfig.text"
                                                        size="medium"
                                                        class="date-input"
                                                        :e2e="`holding-start-date-${rowIndex}`"
                                                        :error="errorToggle && tooltipTrigger === rowData.securityId + 'sd'"
                                                        :ref="rowData.securityId + 'sd'"
                                                        :id="rowData.securityId + 'sd'"
                                                        :mwc-id="rowData.securityId + 'sd'"
                                                        :value="new Date(rowData.startDate)"
                                                        @input="e => errorToggle && validateStartDateInput(e, rowIndex, rowData)"
                                                        @dateBlur="e => changeStartDate(e, rowIndex, rowData)"
                                                    ></hypo-date-input>
                                                </template>
                                                <template v-else>
                                                    {{ formatter.format(new Date(rowData.startDate), "date") }}
                                                </template>
                                            </template>
                                            <template v-else-if="headerConfig.fieldName === 'endDate'">
                                                <!-- Solved HYPO-5127-->
                                                <template v-if="rowData.timePeriod === 0 && !isSimpleAssumptionWorkflow && hypoType !== 2">
                                                    <hypo-date-input
                                                        :key="rowIndex"
                                                        :label="headerConfig.text"
                                                        size="medium"
                                                        class="date-input"
                                                        :e2e="`holding-end-date-${rowIndex}`"
                                                        :error="errorToggle && tooltipTrigger === rowData.securityId + 'ed'"
                                                        :ref="rowData.securityId + 'ed'"
                                                        :id="rowData.securityId + 'ed'"
                                                        :mwc-id="rowData.securityId + 'ed'"
                                                        :value="new Date(rowData.endDate)"
                                                        @input="e => errorToggle && validateEndDateInput(e, rowIndex, rowData)"
                                                        @dateBlur="e => changeEndDate(e, rowIndex, rowData)"
                                                    ></hypo-date-input>
                                                </template>
                                                <template v-else>
                                                    {{ formatter.format(new Date(rowData.endDate), "date") }}
                                                </template>
                                            </template>
                                            <template v-else-if="headerConfig.fieldName === 'availableTimePeriod'">
                                                <template>
                                                    {{ formatter.format(new Date(rowData.firstPriceDate), "date") }}&ndash;{{
                                                        securityRecentMonthEndDate(rowData)
                                                    }}
                                                </template>
                                            </template>
                                        </template>
                                    </mds-data-table-body>
                                </template>
                            </mds-data-table>
                        </div>
                    </div>
                </mds-section>
            </mds-col>
        </mds-row>
        <mds-tooltip v-if="errorToggle" variation="error" :triggered-by="tooltipTrigger" v-model="errorToggle" :position="['bottom-center']">
            <span v-if="tooltipType == dateErrorStatus.invalidDate">{{ this.getUILabel("time_period_tooltip_invalid_date_format") }}</span>
            <span v-if="tooltipType == dateErrorStatus.latestAvailable"
                >{{ this.getUILabel("time_period_tooltip_end_not_available") }}
                <template v-if="tooltipTrigger === portfolioEndDateId">
                    <a @click="setToMostRecentCommon" class="tooltip-link">{{ this.getUILabel("time_period_tooltip_most_recent_common") }}</a>
                </template>
                <template v-else>
                    <a @click="handleTooltipClick" class="tooltip-link">{{ this.getUILabel("time_period_tooltip_most_recent_available") }}</a>
                </template>
            </span>
            <span v-if="tooltipType == dateErrorStatus.beforeStartdate">{{ this.getUILabel("time_period_tooltip_end_before_start") }}</span>
            <span v-if="tooltipType == dateErrorStatus.beforeEarliest">
                <template v-if="tooltipTrigger === portfolioStartDateId">
                    {{
                        isApplyEarliestAvailable
                            ? this.getUILabel("time_period_tooltip_start_before_earliest")
                            : this.getUILabel("tp_tooltip_start_before_earliest_common")
                    }}
                    <a @click="setToMinStartDate" class="tooltip-link">{{
                        isApplyEarliestAvailable ? this.getUILabel("time_period_tooltip_earliest_available") : this.getUILabel("tp_tooltip_earliest_common")
                    }}</a>
                </template>
                <template v-else>
                    {{ this.getUILabel("time_period_tooltip_start_before_earliest") }}
                    <a @click="handleTooltipClick" class="tooltip-link">{{ this.getUILabel("time_period_tooltip_earliest_available") }}</a>
                </template>
            </span>
            <span v-if="tooltipType == dateErrorStatus.afterEnddate">{{ this.getUILabel("time_period_tooltip_start_after_end") }}</span>
        </mds-tooltip>
    </mds-layout-grid>
</template>

<script>
import HypoVueBaseMixin from "hypo-vue-base-mixin";
import * as types from "../store/mutation-types.js";
import { getAvailableMonthEndDate, isDateFormatValid, getDateString } from "@/util.js";
import {
    isPortfolioTimePeriodNotAvailable,
    isSecurityTimePeriodNotAvailable,
    getSecuritiesDateRangeByTimePeriod,
    setSecuritiesDatesByStartDate,
    setSecuritiesDatesByEndDate,
    getSecuritiesDates
} from "@/common/timePeriod.js";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import MdsForm from "@mds/form";
import MdsFieldset from "@mds/fieldset";
import MdsSelect from "@mds/select";
import MdsTooltip from "@mds/tooltip";
import MdsInput from "@mds/input";
import MdsRadioButton from "@mds/radio-button";
import MdsSection from "@mds/section";
import { MdsDataTable, MdsDataTableBody } from "@mds/data-table";
import MdsLoader from "@mds/loader";
import { MdsLayoutGrid, MdsRow, MdsCol } from "@mds/layout-grid";
import HypoDateInput from "hypo-date-input/src/DateInput";

export default {
    name: "holdings",
    props: {
        isPortfolioComparison: {
            type: Boolean,
            default: false
        },
        portfolioList: Array,
        globalAvailableDates: Object,
        globalTimePeriod: [Number, String],
        customTimePeriodDates: {
            type: Object,
            default: () => ({
                startDate: "",
                endDate: ""
            })
        }
    },
    components: {
        MdsForm,
        MdsFieldset,
        MdsSelect,
        MdsTooltip,
        MdsInput,
        MdsRadioButton,
        MdsSection,
        MdsDataTable,
        MdsDataTableBody,
        MdsLoader,
        MdsLayoutGrid,
        MdsRow,
        MdsCol,
        HypoDateInput
    },
    mixins: [HypoVueBaseMixin, MwcSharedMixin],
    data() {
        return {
            defaultConfig: {
                labels: this.$store.state.labels.labels,
                format: {
                    date: {
                        dataType: "date",
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit"
                    }
                }
            },
            dateErrorStatus: {
                beforeEarliest: "BEFORE_EARLIEST",
                latestAvailable: "LATEST_AVAILABLE",
                invalidDate: "INVALID_DATE",
                beforeStartdate: "BEFORE_STARTDATE",
                afterEnddate: "AFTER_ENDDATE"
            },
            errorToggle: false,
            fixDateData: { secIndex: "", fixedValue: "", sec: {} },
            tooltipTrigger: "",
            tooltipType: "",
            showLoading: false,
            columnFields: [],
            tableData: [],
            portfolioTimePeriod: this.globalTimePeriod,
            portfolioStartDateId: "portfolioStartDate",
            portfolioEndDateId: "portfolioEndDate",
            customTimePeriodDateRange: {
                startDate: "",
                endDate: ""
            }
        };
    },
    computed: {
        hypoType() {
            return this.$store.getters["settings/getHypoType"];
        },
        sectionTitle() {
            return this.hypoType === 2 ? this.getUILabel("holdings_section_investment_flow") : this.getUILabel("holdings_section");
        },
        securities() {
            return this.$store.getters["holdings/securitiesWithAllocation"];
        },
        currentPortfolioId() {
            return this.$store.state.settings.rawPortfolio.portfolioId;
        },
        minStartDate() {
            if (this.isApplyEarliestAvailable) {
                const { minEarliestAvailableDate } = getSecuritiesDates(this.securities);
                let mostRecentEarliestAvailableDate = minEarliestAvailableDate;
                const comparisonPortfolio = this.portfolioList.find(portfolio => portfolio.portfolioId !== this.currentPortfolioId);
                if (this.isPortfolioComparison && comparisonPortfolio && comparisonPortfolio.securities.length > 0) {
                    //for portfolio comparison, start date logic use earliest common.
                    const { minEarliestAvailableDate: comparedMinEarliestAvailableDate } = getSecuritiesDates(comparisonPortfolio.securities);
                    if (new Date(mostRecentEarliestAvailableDate) < new Date(comparedMinEarliestAvailableDate)) {
                        mostRecentEarliestAvailableDate = comparedMinEarliestAvailableDate;
                    }
                }
                return mostRecentEarliestAvailableDate;
            } else {
                const { earliestCommonDate } = this.globalAvailableDates;
                return earliestCommonDate;
            }
        },
        timePeriodOptions() {
            return this.$store.getters["holdings/timePeriodOptions"];
        },
        hasCommonEndDate() {
            const { earliestCommonDate, commonEndDate } = this.globalAvailableDates;
            return new Date(earliestCommonDate) < new Date(commonEndDate);
        },
        isPortfolioDateEnabled() {
            return this.isCustomTimePeriod && this.hasCommonEndDate;
        },
        dataInputPlaceholder() {
            return this.isPortfolioDateEnabled ? "MM/DD/YYYY" : undefined;
        },
        isMultipleTimePeriod() {
            return this.portfolioTimePeriod === -1;
        },
        isCustomTimePeriod() {
            return this.portfolioTimePeriod === 0;
        },
        isSimpleAssumptionWorkflow() {
            return this.$store.getters["settings/isSimpleAssumptionWorkflow"];
        },
        isApplyEarliestAvailable() {
            return this.hypoType !== 2 ? this.$store.getters["settings/isEarliestAvailable"] : false;
        },
        portfolioTimePeriodOptions() {
            const earliestCommonOption = this.timePeriodOptions.filter(item => item.value === 7);
            const disabledForInvesmentFLow = [5, 6, 7];
            const otherOptions = this.timePeriodOptions.filter(item =>
                this.hypoType !== 2 ? item.value !== 7 : disabledForInvesmentFLow.indexOf(item.value) < 0
            );
            return [...earliestCommonOption, ...otherOptions]
                .filter(item => {
                    return !((this.isSimpleAssumptionWorkflow || this.isPortfolioComparison) && [5, 6].includes(item.value));
                })
                .map(item => {
                    const { isPreset = false, presetTimePeriod } = item;
                    return {
                        text: this.getUILabel(item.labelId),
                        value: item.value,
                        isPreset: isPreset,
                        presetTimePeriod: presetTimePeriod,
                        disabled: isPortfolioTimePeriodNotAvailable({
                            portfolioList: this.portfolioList,
                            timePeriod: isPreset ? 99 : item.value,
                            presetTimePeriod: presetTimePeriod,
                            isApplyEarliestAvailable: this.isApplyEarliestAvailable
                        })
                    };
                });
        },
        securityTimePeriodOptions() {
            const { earliestCommonDate, commonEndDate } = this.globalAvailableDates;
            return function(security) {
                return this.timePeriodOptions.map(item => {
                    const { isPreset = false, presetTimePeriod } = item;
                    return {
                        text: this.getUILabel(item.labelId),
                        value: item.value,
                        isPreset: isPreset,
                        presetTimePeriod,
                        disabled: isSecurityTimePeriodNotAvailable({
                            security,
                            timePeriod: isPreset ? 99 : item.value,
                            earliestCommonDate,
                            presetTimePeriod: presetTimePeriod,
                            commonEndDate
                        })
                    };
                });
            };
        }
    },
    created() {
        this.showLoading = true;
        this.$store.dispatch("holdings/getSecurityLevelAssetAllocation", { fetch: this.$fetch }).then(() => {
            this.showLoading = false;
            this.initTable();
            this.setCustomTimePeriodRange();
        });
    },
    watch: {
        securities() {
            this.setTableData();
            setTimeout(() => {
                this.portfolioTimePeriod = this.globalTimePeriod;
            }, 500);
        },
        isCustomTimePeriod() {
            if (!this.isCustomTimePeriod) {
                this.errorToggle = false;
                this.unblockPage && this.unblockPage();
            }
        },
        customTimePeriodDates() {
            this.setCustomTimePeriodRange();
        }
    },
    methods: {
        initTable() {
            this.columnFields = [
                {
                    text: this.getUILabel("holdings_name"),
                    fieldName: "securityName",
                    width: "300px"
                },
                {
                    text: this.getUILabel("holdings_time_period"),
                    fieldName: "timePeriod",
                    width: "200px"
                },
                {
                    text: this.getUILabel("holdings_start_date"),
                    fieldName: "startDate",
                    width: "100px",
                    align: "right"
                },
                {
                    text: this.getUILabel("holdings_end_date"),
                    fieldName: "endDate",
                    width: "100px",
                    align: "right"
                },
                {
                    text: "Available Time Period",
                    fieldName: "availableTimePeriod",
                    width: "180px",
                    align: "right"
                },
                {
                    text: "Inception",
                    fieldName: "inceptionDate",
                    width: "100px",
                    align: "right",
                    transform: text => {
                        return this.formatter.format(new Date(text), "date");
                    }
                }
            ];
            this.setTableData();
        },
        setTableData() {
            this.tableData = this.securities.map((sec, index) => {
                return {
                    ...sec,
                    id: index
                };
            });
        },
        securityRecentMonthEndDate(security) {
            const recentMonthEndDate = getAvailableMonthEndDate(security.lastPriceDate);
            return this.formatter.format(new Date(recentMonthEndDate), "date");
        },
        securityTimePeriodOption(security) {
            const securityTimePeriodOptions = this.securityTimePeriodOptions(security);
            const { timePeriod, presetTimePeriod } = security;
            return securityTimePeriodOptions.find(item => {
                const timePeriodValue = presetTimePeriod ? `${timePeriod}-${presetTimePeriod.type}` : timePeriod;
                return item.value === timePeriodValue;
            });
        },
        securityTimePeriod(security) {
            const timePeriodOption = this.securityTimePeriodOption(security);
            return timePeriodOption && timePeriodOption.value;
        },
        securityTimePeriodText(security) {
            const timePeriodOption = this.securityTimePeriodOption(security);
            return timePeriodOption && timePeriodOption.text;
        },
        setCustomTimePeriodRange() {
            let { startDate, endDate } = this.customTimePeriodDates;
            // reset portfolio level custom start date to empty if custom start date is before global valid custom start date.
            if (startDate && new Date(startDate) < new Date(this.minStartDate)) {
                startDate = "";
            }
            this.customTimePeriodDateRange.startDate = startDate === "" ? startDate : this.formatDateField(startDate);
            this.customTimePeriodDateRange.endDate = endDate === "" ? endDate : this.formatDateField(endDate);
        },
        getDateRange({ value: timePeriod, isPreset = false, presetTimePeriod }) {
            if (timePeriod === 0) {
                return undefined;
            }
            let dateRange = null;
            if (isPreset) {
                dateRange = {
                    startDate: presetTimePeriod.startDate,
                    endDate: presetTimePeriod.endDate
                };
            } else {
                dateRange = getSecuritiesDateRangeByTimePeriod(this.securities, timePeriod, this.globalAvailableDates);
            }
            if (dateRange === null) {
                return "";
            }
            const { startDate, endDate } = dateRange;
            const strStartDate = this.formatDateField(startDate);
            const strEndDate = this.formatDateField(endDate);
            return `${strStartDate}&ndash;${strEndDate}`;
        },
        changeTimePeriod(value, { isPreset = false, presetTimePeriod }) {
            let [timePeriod] = value.toString().split("-");
            timePeriod = Number(timePeriod);
            let payload = {
                timePeriod
            };
            if (isPreset) {
                payload = {
                    ...payload,
                    presetTimePeriod
                };
            }
            this.$emit("global-time-period-changed", payload);
        },
        changeSecurityTimePeriod(value, security) {
            const selectTimePeriod = value;
            const timePeriodOptions = this.securityTimePeriodOptions(security);
            const { presetTimePeriod } = timePeriodOptions.find(i => i.value.toString() === selectTimePeriod);
            let [timePeriod] = selectTimePeriod.split("-");
            timePeriod = Number(timePeriod);
            let payload = {
                portfolioId: this.currentPortfolioId,
                securityId: security.securityId,
                timePeriod: timePeriod
            };
            if (presetTimePeriod) {
                payload = {
                    ...payload,
                    presetTimePeriod
                };
            }

            this.$emit("security-time-period-changed", payload);
        },
        changeStartDate(value, secIndex, row) {
            this.errorToggle = false;
            this.tooltipTrigger = `${row.securityId}sd`;
            const target = this.$refs[this.tooltipTrigger].$el;
            const sec = { ...this.securities[secIndex] };
            if (this.validDate(value)) {
                if (value < new Date(sec.firstPriceDate)) {
                    this.tooltipType = this.dateErrorStatus.beforeEarliest;
                    this.errorToggle = true;
                    this.fixDateData = { secIndex, sec, fixedValue: sec.firstPriceDate };
                } else if (value > new Date(sec.endDate)) {
                    this.tooltipType = this.dateErrorStatus.afterEnddate;
                    this.errorToggle = true;
                }
                sec.startDate = getDateString(value);
            } else {
                this.tooltipType = this.dateErrorStatus.invalidDate;
                this.errorToggle = true;
            }
            if (!this.errorToggle) {
                document.removeEventListener("mousedown", this.blockEvent, true);
                document.removeEventListener("click", this.blockEvent, true);
                const newSecs = this.securities.map((item, index) => {
                    return index === secIndex ? sec : item;
                });
                this.$store.commit(`holdings/${types.SET_SECURITIES}`, newSecs);
                this.$emit("security-time-period-changed", {
                    portfolioId: this.currentPortfolioId,
                    securityId: sec.securityId,
                    timePeriod: 0,
                    defaultDateRange: {
                        startDate: sec.startDate,
                        endDate: sec.endDate
                    }
                });
            } else {
                document.addEventListener("click", this.blockEvent, true);
                document.addEventListener("mousedown", this.blockEvent, true);
                target.focus();
            }
        },
        changeEndDate(value, secIndex, row) {
            this.errorToggle = false;
            this.tooltipTrigger = `${row.securityId}ed`;
            const target = this.$refs[this.tooltipTrigger].$el;
            const sec = { ...this.securities[secIndex] };
            const latestAvailableDate = getAvailableMonthEndDate(sec.lastPriceDate);
            if (this.validDate(value)) {
                if (value > new Date(latestAvailableDate)) {
                    this.tooltipType = this.dateErrorStatus.latestAvailable;
                    this.errorToggle = true;
                    this.fixDateData = { secIndex, sec, fixedValue: latestAvailableDate };
                } else if (value < new Date(sec.startDate)) {
                    this.tooltipType = this.dateErrorStatus.beforeStartdate;
                    this.errorToggle = true;
                }
                sec.endDate = getDateString(value);
            } else {
                this.tooltipType = this.dateErrorStatus.invalidDate;
                this.errorToggle = true;
            }

            if (!this.errorToggle) {
                const newSecs = this.securities.map((item, index) => {
                    return index === secIndex ? sec : item;
                });
                this.$store.commit(`holdings/${types.SET_SECURITIES}`, newSecs);
                document.removeEventListener("mousedown", this.blockEvent, true);
                document.removeEventListener("click", this.blockEvent, true);
            } else {
                document.addEventListener("click", this.blockEvent, true);
                document.addEventListener("mousedown", this.blockEvent, true);
                target.focus();
            }
        },
        formatDateField(date) {
            let newDate = "";
            try {
                newDate = this.formatter.format(this.formatter.format(new Date(date), "date"));
            } catch (error) {
                newDate = date;
            }
            return newDate;
        },
        handleTooltipClick() {
            if (this.tooltipType === this.dateErrorStatus.beforeEarliest) {
                this.fixDateData.sec.startDate = this.fixDateData.fixedValue;
            } else if (this.tooltipType === this.dateErrorStatus.latestAvailable) {
                this.fixDateData.sec.endDate = this.fixDateData.fixedValue;
            }
            const newSecs = this.securities.map((item, index) => {
                return index === this.fixDateData.secIndex ? this.fixDateData.sec : item;
            });
            this.$store.commit(`holdings/${types.SET_SECURITIES}`, newSecs);
            this.errorToggle = false;
            document.removeEventListener("mousedown", this.blockEvent, true);
            document.removeEventListener("click", this.blockEvent, true);
        },
        blockEvent(e) {
            if (
                e.target &&
                e.target.closest &&
                e.target.closest(".date-input") === null &&
                e.target.className !== "tooltip-link" &&
                e.target.id !== this.tooltipTrigger
            ) {
                e.stopPropagation();
                e.preventDefault();
            }
        },
        validDate(value) {
            const testDate = new Date(value);
            if (value === null || testDate.toString() === "Invalid Date") {
                return false;
            }
            return true;
        },
        excludeInvalidChar(e) {
            const keyCode = e.keyCode;
            //only allow number,slash,hyphen char.
            if (keyCode !== 45 && (e.keyCode < 47 || e.keyCode > 57)) {
                e.preventDefault();
            }
        },
        validateStartDate(e) {
            const { commonEndDate } = this.globalAvailableDates;
            const strStartDate = e.target.value.trim();
            this.customTimePeriodDateRange.startDate = strStartDate;
            if (strStartDate === "") {
                this.errorToggle = false;
                this.unblockPage && this.unblockPage();
                return;
            }
            const minStartDate = new Date(this.minStartDate);
            const { endDate } = this.customTimePeriodDateRange;
            let maxStartDate;
            if (endDate) {
                maxStartDate = new Date(endDate);
            } else {
                maxStartDate = new Date(commonEndDate);
            }
            let hasError = false,
                type = "";
            const startDate = new Date(strStartDate);
            if (!isDateFormatValid(strStartDate)) {
                hasError = true;
                type = this.dateErrorStatus.invalidDate;
            } else {
                this.customTimePeriodDateRange.startDate = this.formatDateField(strStartDate);
                if (startDate < minStartDate) {
                    hasError = true;
                    type = this.dateErrorStatus.beforeEarliest;
                } else if (startDate > maxStartDate) {
                    hasError = true;
                    type = this.dateErrorStatus.afterEnddate;
                }
            }

            this.errorToggle = hasError;
            if (hasError) {
                this.tooltipTrigger = this.portfolioStartDateId;
                this.tooltipType = type;
                if (!this.unblockPage) {
                    const elements = this.$refs.timePeriodRadios.map(i => i.$el);
                    this.unblockPage = this.blockPage(e.target, elements, () => {
                        e.target.focus();
                    });
                }
            } else {
                this.unblockPage && this.unblockPage();
                this.updateSecuritiesStartDate(startDate);
            }
        },
        validateStartDateInput(value, secIndex, row) {
            const sec = { ...this.securities[secIndex] };
            if (this.validDate(value)) {
                if (value < new Date(sec.firstPriceDate)) {
                    this.tooltipType = this.dateErrorStatus.beforeEarliest;
                    this.fixDateData = { secIndex, sec, fixedValue: sec.firstPriceDate };
                } else if (value > new Date(sec.endDate)) {
                    this.tooltipType = this.dateErrorStatus.afterEnddate;
                } else {
                    // No error with new date
                    this.errorToggle = false;
                    sec.startDate = getDateString(value);
                    document.removeEventListener("mousedown", this.blockEvent, true);
                    document.removeEventListener("click", this.blockEvent, true);
                    const newSecs = this.securities.map((item, index) => {
                        return index === secIndex ? sec : item;
                    });
                    this.$store.commit(`holdings/${types.SET_SECURITIES}`, newSecs);
                }
            } else {
                this.tooltipType = this.dateErrorStatus.invalidDate;
            }
        },
        validateEndDate(e) {
            const { commonEndDate } = this.globalAvailableDates;
            const strEndDate = e.target.value.trim();
            this.customTimePeriodDateRange.endDate = strEndDate;
            if (strEndDate === "") {
                this.errorToggle = false;
                this.unblockPage && this.unblockPage();
                return;
            }
            const { startDate } = this.customTimePeriodDateRange;
            let minEndDate;
            if (startDate) {
                minEndDate = new Date(startDate);
            } else {
                minEndDate = new Date(this.minStartDate);
            }
            const maxEndDate = new Date(commonEndDate);
            const endDate = new Date(strEndDate);
            let hasError = false,
                type = "";
            if (!isDateFormatValid(strEndDate)) {
                hasError = true;
                type = this.dateErrorStatus.invalidDate;
            } else {
                this.customTimePeriodDateRange.endDate = this.formatDateField(strEndDate);
                if (endDate < minEndDate) {
                    hasError = true;
                    type = this.dateErrorStatus.beforeStartdate;
                } else if (endDate > maxEndDate) {
                    hasError = true;
                    type = this.dateErrorStatus.latestAvailable;
                }
            }

            this.errorToggle = hasError;
            if (hasError) {
                this.tooltipTrigger = this.portfolioEndDateId;
                this.tooltipType = type;
                if (!this.unblockPage) {
                    const elements = this.$refs.timePeriodRadios.map(i => i.$el);
                    this.unblockPage = this.blockPage(e.target, elements, () => {
                        e.target.focus();
                    });
                }
            } else {
                this.unblockPage && this.unblockPage();
                this.updateSecuritiesEndDate(endDate);
            }
        },
        validateEndDateInput(value, secIndex, row) {
            const sec = { ...this.securities[secIndex] };
            const latestAvailableDate = getAvailableMonthEndDate(sec.lastPriceDate);
            if (this.validDate(value)) {
                if (value > new Date(latestAvailableDate)) {
                    this.tooltipType = this.dateErrorStatus.latestAvailable;
                    this.fixDateData = { secIndex, sec, fixedValue: latestAvailableDate };
                } else if (value < new Date(sec.startDate)) {
                    this.tooltipType = this.dateErrorStatus.beforeStartdate;
                } else {
                    // No error with new date
                    this.errorToggle = false;
                    sec.endDate = getDateString(value);
                    document.removeEventListener("mousedown", this.blockEvent, true);
                    document.removeEventListener("click", this.blockEvent, true);
                    const newSecs = this.securities.map((item, index) => {
                        return index === secIndex ? sec : item;
                    });
                    this.$store.commit(`holdings/${types.SET_SECURITIES}`, newSecs);
                }
            } else {
                this.tooltipType = this.dateErrorStatus.invalidDate;
            }
        },
        blockPage(currentTarget, excludedElements, callback) {
            const isExcludedElements = el => {
                return excludedElements.some(i => {
                    return i.contains(el);
                });
            };
            const handler = e => {
                let currentNode = e.target;
                const inputReg = /^(?:input|select|textarea|button)$/i;
                while (currentNode !== null && !inputReg.test(currentNode.tagName)) {
                    currentNode = currentNode.parentNode;
                }
                const isSelf = currentTarget === currentNode;
                const isBlockEl = !isExcludedElements(currentNode) && !isSelf;
                if (currentNode && isBlockEl) {
                    e.stopPropagation();
                    e.preventDefault();
                }
                callback();
            };
            document.addEventListener("mousedown", handler, true);
            document.addEventListener("click", handler, true);
            return () => {
                document.removeEventListener("mousedown", handler, true);
                document.removeEventListener("click", handler, true);
            };
        },
        setToMinStartDate() {
            this.customTimePeriodDateRange.startDate = this.formatDateField(this.minStartDate);
            const startDate = new Date(this.minStartDate);
            this.updateSecuritiesStartDate(startDate);
            this.errorToggle = false;
            this.unblockPage && this.unblockPage();
        },
        setToMostRecentCommon() {
            const { commonEndDate } = this.globalAvailableDates;
            this.customTimePeriodDateRange.endDate = this.formatDateField(commonEndDate);
            const endDate = new Date(commonEndDate);
            this.updateSecuritiesEndDate(endDate);
            this.errorToggle = false;
            this.unblockPage && this.unblockPage();
        },
        updateSecuritiesStartDate(startDate) {
            const newSecs = setSecuritiesDatesByStartDate({
                securities: this.securities,
                startDate
            });
            this.$store.commit(`holdings/${types.SET_SECURITIES}`, newSecs);
            this.$emit("global-time-period-changed", {
                timePeriod: 0,
                defaultDateRange: {
                    startDate: getDateString(startDate),
                    endDate: newSecs[0].endDate
                }
            });
        },
        updateSecuritiesEndDate(endDate) {
            const oldSecurities = this.securities.map(i => {
                return {
                    ...i
                };
            });
            const newSecs = setSecuritiesDatesByEndDate({
                securities: oldSecurities,
                endDate
            });
            this.$store.commit(`holdings/${types.SET_SECURITIES}`, newSecs);
            this.$emit("global-time-period-changed", {
                timePeriod: 0,
                defaultDateRange: {
                    startDate: newSecs[0].startDate,
                    endDate: getDateString(endDate)
                }
            });
        },
        cleanFixedFirstCol() {
            const fixedTableRows = document.querySelectorAll(".holdings .mds-data-table--fixed-column___hypo .mds-data-table__row___hypo");
            const keepCols = 2;
            [...fixedTableRows].forEach(row => {
                for (let i = keepCols; i < row.children.length; i++) {
                    // Deleting headers might cause table rows mismatch
                    if (!row.children[i].className.includes("header")) {
                        row.children[i].remove();
                    }
                }
            });
        }
    },
    beforeDestroy() {
        this.unblockPage && this.unblockPage();
    }
};
</script>

<style lang="scss" scoped>
@import "~@mds/typography";

// Fixed table mess up after cleanFixedFirstCol
::v-deep .mds-data-table__cell___hypo {
    line-height: 35px;
}
::v-deep .mds-data-table__cell-numbered___hypo {
    line-height: 35px;
}

.holdings {
    &-description {
        @include mds-body-text-m();
        color: $mds-text-color-primary;
        margin-bottom: $mds-space-2-x;
    }

    &-settings {
        margin-bottom: $mds-space-4-x;
        > .mds-fieldset___hypo:first-child {
            margin-bottom: $mds-space-three-quarter-x;
        }
        > .mds-fieldset___hypo:nth-child(2) {
            margin-left: 22px;
            .mds-label___hypo {
                margin-bottom: $mds-space-three-quarter-x;
            }
        }
    }

    .input--disabled {
        opacity: 0.3;
    }

    &-holdings-table {
        /deep/ .mds-data-table__table___hypo {
            .mds-data-table__cell-numbered___hypo {
                padding-top: 3px;
            }
            .mds-data-table__cell___hypo {
                padding-top: $mds-space-quarter-x;
                padding-bottom: $mds-space-quarter-x;
            }
            .name {
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
            }
        }
    }
}
a.tooltip-link {
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
}
</style>
